import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type { Buyer, BuyerContact, BuyerProduct, Invoice, InvoiceLineItem, MenuItem } from '@/types/app'

export function useBuyers() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.buyers(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('buyers')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('name')
      if (error) throw error
      return (data ?? []) as Buyer[]
    },
    enabled: !!restaurantId,
  })
}

export function useSaveBuyer() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Buyer> & { id?: string }) => {
      if (payload.id) {
        const { data, error } = await supabase
          .from('buyers')
          .update(payload)
          .eq('id', payload.id)
          .select()
          .single()
        if (error) throw error
        return data as Buyer
      }
      const { data, error } = await supabase
        .from('buyers')
        .insert({ ...payload, restaurant_id: restaurantId })
        .select()
        .single()
      if (error) throw error
      return data as Buyer
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.buyers(restaurantId) }),
  })
}

export interface BuyerAssociations {
  invoices: { id: string; invoice_number: string; status: string }[]
  contacts: { id: string; name: string }[]
}

export async function fetchBuyerAssociations(buyerId: string): Promise<BuyerAssociations> {
  const [{ data: invoices }, { data: contacts }] = await Promise.all([
    supabase.from('invoices').select('id, invoice_number, status').eq('buyer_id', buyerId),
    supabase.from('buyer_contacts').select('id, name').eq('buyer_id', buyerId),
  ])
  return {
    invoices: (invoices ?? []) as BuyerAssociations['invoices'],
    contacts: (contacts ?? []) as BuyerAssociations['contacts'],
  }
}

export function useDeleteBuyer() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (buyerId: string) => {
      // Cascade: sequence entries → contacts → buyer
      await supabase.from('invoice_number_seq').delete().eq('buyer_id', buyerId)
      await supabase.from('buyer_contacts').delete().eq('buyer_id', buyerId)
      const { error } = await supabase.from('buyers').delete().eq('id', buyerId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.buyers(restaurantId) }),
  })
}

export function useInvoices() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.invoices(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, buyer:buyers(*), line_items:invoice_line_items(*)')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as Invoice[]
    },
    enabled: !!restaurantId,
  })
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: queryKeys.invoice(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, buyer:buyers(*), line_items:invoice_line_items(*)')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as Invoice
    },
    enabled: !!id,
  })
}

export function useSaveInvoice() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      invoice,
      lineItems,
    }: {
      invoice: Omit<Partial<Invoice>, 'invoice_number' | 'line_items' | 'buyer'>
      lineItems: Omit<InvoiceLineItem, 'id' | 'invoice_id' | 'total'>[]
    }) => {
      if (invoice.id) {
        // Fetch previous status to detect transition
        const { data: prev } = await supabase
          .from('invoices')
          .select('status')
          .eq('id', invoice.id)
          .single()

        const { data, error } = await supabase
          .from('invoices')
          .update({
            buyer_id: invoice.buyer_id,
            status: invoice.status,
            issued_date: invoice.issued_date,
            due_date: invoice.due_date,
            po_number: invoice.po_number ?? null,
            payment_terms: invoice.payment_terms ?? 'Net 30',
            tax_percent: invoice.tax_percent ?? 0,
            discount_amount: invoice.discount_amount ?? 0,
            amount_paid: invoice.amount_paid ?? 0,
            payment_notes: invoice.payment_notes ?? null,
            notes: invoice.notes ?? null,
          })
          .eq('id', invoice.id)
          .select()
          .single()
        if (error) throw error
        await supabase.from('invoice_line_items').delete().eq('invoice_id', invoice.id)
        if (lineItems.length > 0) {
          const { error: liErr } = await supabase
            .from('invoice_line_items')
            .insert(lineItems.map(li => ({ ...li, invoice_id: invoice.id })))
          if (liErr) throw liErr
        }

        // Sync stock: deduct when sent, restore when cancelled
        await syncInvoiceStock(restaurantId, invoice.id, lineItems, invoice.status, prev?.status)

        return data as Invoice
      }
      const { data, error } = await supabase
        .from('invoices')
        .insert({
          restaurant_id: restaurantId,
          buyer_id: invoice.buyer_id,
          status: invoice.status ?? 'draft',
          issued_date: invoice.issued_date ?? new Date().toISOString().split('T')[0],
          due_date: invoice.due_date ?? null,
          po_number: invoice.po_number ?? null,
          payment_terms: invoice.payment_terms ?? 'Net 30',
          tax_percent: invoice.tax_percent ?? 0,
          discount_amount: invoice.discount_amount ?? 0,
          amount_paid: invoice.amount_paid ?? 0,
          payment_notes: invoice.payment_notes ?? null,
          notes: invoice.notes ?? null,
        })
        .select()
        .single()
      if (error) throw error
      const newId = (data as Invoice).id
      if (lineItems.length > 0) {
        const { error: liErr } = await supabase
          .from('invoice_line_items')
          .insert(lineItems.map(li => ({ ...li, invoice_id: newId })))
        if (liErr) throw liErr
      }

      // Deduct stock immediately if created as sent
      await syncInvoiceStock(restaurantId, newId, lineItems, invoice.status ?? 'draft', undefined)

      return data as Invoice
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.productStock(restaurantId) })
    },
  })
}

async function syncInvoiceStock(
  restaurantId: string,
  invoiceId: string,
  lineItems: Omit<InvoiceLineItem, 'id' | 'invoice_id' | 'total'>[],
  newStatus: string | undefined,
  prevStatus: string | undefined,
) {
  const wasSent = prevStatus === 'sent' || prevStatus === 'partial' || prevStatus === 'paid'
  const isSent  = newStatus  === 'sent' || newStatus  === 'partial' || newStatus  === 'paid'
  const isCancelled = newStatus === 'cancelled'

  if (!isSent && !isCancelled) return   // draft → draft or other non-dispatch statuses: no change

  // Wipe existing invoice stock txns (idempotent)
  await supabase
    .from('product_stock_txns')
    .delete()
    .eq('reference_type', 'invoice')
    .eq('reference_id', invoiceId)

  if (isCancelled) return  // just remove the deductions

  // Re-insert fresh out txns for all linked line items
  const txns = lineItems
    .filter(li => li.menu_item_id && li.qty > 0)
    .map(li => ({
      restaurant_id:  restaurantId,
      menu_item_id:   li.menu_item_id as string,
      txn_type:       'out' as const,
      qty:            li.qty,
      reference_type: 'invoice' as const,
      reference_id:   invoiceId,
      notes:          li.description,
    }))

  if (txns.length > 0) {
    await supabase.from('product_stock_txns').insert(txns)
  }
  void wasSent  // suppress unused-var warning
}

export function useStoreSupplyItems() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.storeSupply(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('available_store_supply', true)
        .eq('is_deleted', false)
        .order('name')
      if (error) throw error
      return (data ?? []) as MenuItem[]
    },
    enabled: !!restaurantId,
  })
}

export function useBuyerInvoices(buyerId: string) {
  return useQuery({
    queryKey: ['invoices', 'buyer', buyerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, line_items:invoice_line_items(*)')
        .eq('buyer_id', buyerId)
        .not('status', 'in', '("paid","cancelled")')
        .order('issued_date', { ascending: true })
      if (error) throw error
      return (data ?? []) as Invoice[]
    },
    enabled: !!buyerId,
  })
}

interface RecordBatchPaymentArgs {
  buyerId: string
  paymentAmount: number
  paymentDate: string
  notes: string
}

export function useRecordBatchPayment() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async ({ buyerId, paymentAmount, paymentDate, notes }: RecordBatchPaymentArgs) => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, line_items:invoice_line_items(*)')
        .eq('buyer_id', buyerId)
        .not('status', 'in', '("paid","cancelled")')
        .order('issued_date', { ascending: true })
      if (error) throw error

      const pending = (data ?? []) as Invoice[]
      let remaining = paymentAmount
      let updatedCount = 0

      for (const invoice of pending) {
        if (remaining <= 0) break

        const subtotal = (invoice.line_items ?? []).reduce(
          (sum, item) => sum + item.qty * item.unit_price,
          0
        )
        const grandTotal = subtotal * (1 + (invoice.tax_percent ?? 0) / 100) - (invoice.discount_amount ?? 0)
        const balanceDue = grandTotal - (invoice.amount_paid ?? 0)

        if (balanceDue <= 0) continue

        if (remaining >= balanceDue) {
          const { error: updateErr } = await supabase
            .from('invoices')
            .update({
              status: 'paid',
              amount_paid: grandTotal,
              payment_notes: notes || null,
            })
            .eq('id', invoice.id)
          if (updateErr) throw updateErr
          remaining -= balanceDue
          updatedCount++
        } else {
          const { error: updateErr } = await supabase
            .from('invoices')
            .update({
              status: 'partial',
              amount_paid: (invoice.amount_paid ?? 0) + remaining,
              payment_notes: notes || null,
            })
            .eq('id', invoice.id)
          if (updateErr) throw updateErr
          remaining = 0
          updatedCount++
          break
        }
      }

      return { updatedCount, paymentDate }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.invoices(restaurantId) })
      qc.invalidateQueries({ queryKey: ['invoices', 'buyer', variables.buyerId] })
    },
  })
}

export function useBuyerContacts(buyerId: string) {
  return useQuery({
    queryKey: queryKeys.buyerContacts(buyerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('buyer_contacts')
        .select('*')
        .eq('buyer_id', buyerId)
        .order('is_primary', { ascending: false })
        .order('created_at')
      if (error) throw error
      return (data ?? []) as BuyerContact[]
    },
    enabled: !!buyerId,
  })
}

export function useSaveBuyerContact() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<BuyerContact> & { buyer_id: string }) => {
      if (payload.id) {
        const { data, error } = await supabase
          .from('buyer_contacts')
          .update({
            name: payload.name,
            role: payload.role ?? null,
            phone: payload.phone ?? null,
            whatsapp: payload.whatsapp ?? null,
            email: payload.email ?? null,
            is_primary: payload.is_primary ?? false,
          })
          .eq('id', payload.id)
          .select()
          .single()
        if (error) throw error
        return data as BuyerContact
      }
      const { data, error } = await supabase
        .from('buyer_contacts')
        .insert({ ...payload, restaurant_id: restaurantId })
        .select()
        .single()
      if (error) throw error
      return data as BuyerContact
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.buyerContacts(variables.buyer_id) })
      qc.invalidateQueries({ queryKey: queryKeys.buyers(restaurantId) })
    },
  })
}

export function useDeleteBuyerContact() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, buyerId }: { id: string; buyerId: string }) => {
      const { error } = await supabase.from('buyer_contacts').delete().eq('id', id)
      if (error) throw error
      return { buyerId }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.buyerContacts(variables.buyerId) })
      qc.invalidateQueries({ queryKey: queryKeys.buyers(restaurantId) })
    },
  })
}

// ── Buyer favorite/contract products ────────────────────────────────────────
// A buyer's product allowlist — what they're actually contracted to buy.
// Invoicing (manual and RezzAI) restricts its product picker to this list
// once one exists, instead of showing the entire catalog.

export function useBuyerProducts(buyerId: string) {
  return useQuery({
    queryKey: queryKeys.buyerProducts(buyerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('buyer_products')
        .select('*, menu_item:menu_items(id, name, upc_code, regional_name, price, wholesale_price)')
        .eq('buyer_id', buyerId)
        .order('created_at')
      if (error) throw error
      return (data ?? []) as (BuyerProduct & { menu_item: MenuItem | null })[]
    },
    enabled: !!buyerId,
  })
}

export function useSaveBuyerProduct() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { buyer_id: string; menu_item_id: string; contract_price?: number | null; notes?: string | null }) => {
      const { data, error } = await supabase
        .from('buyer_products')
        .upsert(
          { ...payload, restaurant_id: restaurantId },
          { onConflict: 'buyer_id,menu_item_id' }
        )
        .select()
        .single()
      if (error) throw error
      return data as BuyerProduct
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.buyerProducts(variables.buyer_id) })
    },
  })
}

export function useDeleteBuyerProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, buyerId }: { id: string; buyerId: string }) => {
      const { error } = await supabase.from('buyer_products').delete().eq('id', id)
      if (error) throw error
      return { buyerId }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.buyerProducts(variables.buyerId) })
    },
  })
}
